import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut, type User } from "firebase/auth";
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "./firebase";
import type { AppUser } from "./types";

interface AuthValue {
  user: User | null;
  profile: AppUser | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => onAuthStateChanged(auth, async (nextUser) => {
    setUser(nextUser);
    if (!nextUser) {
      setProfile(null);
      setLoading(false);
      return;
    }
    const ref = doc(db, "users", nextUser.uid);
    const snapshot = await getDoc(ref);
    let resolvedProfile: AppUser;
    if (!snapshot.exists()) {
      const created: Omit<AppUser, "id"> = {
        email: nextUser.email || "",
        displayName: nextUser.displayName || "Joueur",
        pseudonym: nextUser.displayName || "Détective",
        isAdmin: false,
        createdAt: serverTimestamp() as AppUser["createdAt"],
      };
      await setDoc(ref, created);
      resolvedProfile = { id: nextUser.uid, ...created };
    } else {
      resolvedProfile = { id: nextUser.uid, ...snapshot.data() } as AppUser;
    }
    if (nextUser.email?.toLowerCase() === "marcherelie.application@gmail.com" && !resolvedProfile.isAdmin) {
      try {
        await httpsCallable(functions, "bootstrapAdmin")();
        await nextUser.getIdToken(true);
        resolvedProfile = { ...resolvedProfile, isAdmin: true };
      } catch { /* La fonction sera disponible après le premier déploiement. */ }
    }
    setProfile(resolvedProfile);
    setLoading(false);
  }), []);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, "users", user.uid), snapshot => {
      if (snapshot.exists()) setProfile({ id: snapshot.id, ...snapshot.data() } as AppUser);
    });
  }, [user]);

  const value = useMemo<AuthValue>(() => ({
    user,
    profile,
    loading,
    login: async () => { await signInWithPopup(auth, new GoogleAuthProvider()); },
    logout: async () => { await signOut(auth); },
  }), [user, profile, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth doit être utilisé dans AuthProvider");
  return context;
}
